<script>
  import { onMount } from 'svelte';
  import { API_BASE_URL, isAdminUser } from './config.js';
  import Navigation from './components/layout/Navigation.svelte';

  export let setPage;
  export let user;
  export let onLogout = null;

  // 권한 확인
  let isAdmin = false;
  let isCompany = false;

  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
      } catch (e) {
        console.error('사용자 정보 파싱 오류:', e);
      }
    }
  }

  // 공통 API 응답 처리
  function handleApiResponse(response) {
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setPage('login');
      return false;
    }
    return true;
  }

  // 상태
  let projects = [];
  let loading = true;
  let errorMessage = '';
  let showPreview = true;
  let copySuccess = false;
  let iframeEl;
  let searchText = '';

  // 등급별 배정 맵: { projectId: '초급' | '중급' | '고급' }
  let assignments = {};

  // 사이트 URL (이메일에 포함되는 링크)
  const SITE_URL = 'https://widework.kr';

  // 등급 목록 & 색상
  const levels = ['초급', '중급', '고급'];
  const levelColors = {
    '초급': '#3273dc',
    '중급': '#27ae60',
    '고급': '#e67e22'
  };

  // 드래그 상태
  let draggedProjectId = null;
  let dragOverLevel = null;

  // HTML 이스케이프
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // 날짜 포맷 (YYYY-MM-DD → MM/DD)
  function formatDate(dateStr) {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length >= 3) return `${parts[1]}/${parts[2]}`;
    return dateStr;
  }

  // 날짜 포맷 (YYYY-MM-DD → YYYY.MM.DD)
  function formatDateFull(dateStr) {
    if (!dateStr) return '-';
    return dateStr.replace(/-/g, '.');
  }

  // 프로젝트 로드
  async function loadProjects() {
    loading = true;
    errorMessage = '';
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects?excludeExpired=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!handleApiResponse(response)) return;

      const data = await response.json();
      if (data.success) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        projects = (data.projects || []).filter(p => {
          if (p.status === 'deleted' || p.status === 'lapse' || p.status === 'closed') return false;
          if (p.deadline) {
            const dl = new Date(p.deadline);
            if (dl < today) return false;
          }
          return true;
        });
      } else {
        errorMessage = data.error || '프로젝트를 불러오지 못했습니다.';
      }
    } catch (err) {
      console.error('프로젝트 로드 오류:', err);
      errorMessage = '서버 연결에 실패했습니다.';
    } finally {
      loading = false;
    }
  }

  // 프로젝트를 등급에 배정
  function assignToLevel(projectId, level) {
    if (assignments[projectId] === level) {
      // 같은 등급 클릭 시 해제
      delete assignments[projectId];
    } else {
      assignments[projectId] = level;
    }
    assignments = assignments; // 반응성 트리거
  }

  // 등급에서 프로젝트 제거
  function removeFromLevel(projectId) {
    delete assignments[projectId];
    assignments = assignments;
  }

  // 전체 해제
  function clearAll() {
    assignments = {};
  }

  // 미배정 프로젝트 (검색 필터 적용)
  $: filteredProjects = projects.filter(p => {
    if (searchText) {
      const q = searchText.toLowerCase();
      return (p.title || '').toLowerCase().includes(q) ||
             (p.location || '').toLowerCase().includes(q) ||
             (p.level || '').toLowerCase().includes(q);
    }
    return true;
  });

  // 등급별 배정된 프로젝트
  $: assignedByLevel = {
    '초급': projects.filter(p => assignments[p.id] === '초급'),
    '중급': projects.filter(p => assignments[p.id] === '중급'),
    '고급': projects.filter(p => assignments[p.id] === '고급')
  };

  // 배정된 총 수
  $: totalAssigned = Object.keys(assignments).length;

  // --- 드래그 앤 드롭 ---
  function handleDragStart(e, projectId) {
    draggedProjectId = projectId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(projectId));
  }

  function handleDragOver(e, level) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverLevel = level;
  }

  function handleDragLeave(e, level) {
    if (dragOverLevel === level) {
      dragOverLevel = null;
    }
  }

  function handleDrop(e, level) {
    e.preventDefault();
    dragOverLevel = null;
    if (draggedProjectId != null) {
      assignments[draggedProjectId] = level;
      assignments = assignments;
      draggedProjectId = null;
    }
  }

  function handleDragEnd() {
    draggedProjectId = null;
    dragOverLevel = null;
  }

  // --- 이메일 HTML 생성 ---
  function generateEmailHtml() {
    const activeLevels = levels.filter(l => assignedByLevel[l].length > 0);

    if (activeLevels.length === 0) {
      return '<p style="text-align:center; color:#999; padding:40px;">선택된 프로젝트가 없습니다.</p>';
    }

    let sectionsHtml = '';

    for (const level of activeLevels) {
      const color = levelColors[level];
      const items = assignedByLevel[level];

      let rowsHtml = '';
      for (const p of items) {
        const projectUrl = `${SITE_URL}/#project/${p.id}`;
        const period = (p.start_date && p.end_date)
          ? `${formatDate(p.start_date)}~${formatDate(p.end_date)}`
          : '-';

        rowsHtml += `
              <tr>
                <td style="padding:10px 12px; border-bottom:1px solid #eee; font-size:14px;">
                  <a href="${escapeHtml(projectUrl)}" style="color:#2c3e50; text-decoration:none; font-weight:500;" target="_blank">
                    ${escapeHtml(p.title)}
                  </a>
                </td>
                <td style="padding:10px 12px; border-bottom:1px solid #eee; font-size:12px; color:#555; text-align:center; word-break:keep-all;">
                  ${escapeHtml(p.location || '-')}
                </td>
                <td style="padding:10px 12px; border-bottom:1px solid #eee; font-size:13px; color:#555; text-align:center; white-space:nowrap;">
                  ${period}
                </td>
                <td style="padding:10px 12px; border-bottom:1px solid #eee; font-size:13px; color:#555; text-align:center; white-space:nowrap;">
                  ${p.deadline ? formatDateFull(p.deadline) : '-'}
                </td>
              </tr>`;
      }

      sectionsHtml += `
          <!-- ${escapeHtml(level)} 섹션 -->
          <tr>
            <td style="padding:0;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:4px;">
                <tr>
                  <td style="background-color:${color}; color:#fff; padding:10px 16px; font-size:15px; font-weight:bold; border-radius:4px 4px 0 0;">
                    ${escapeHtml(level)} 프로젝트 (${items.length}건)
                  </td>
                </tr>
                <tr>
                  <td style="padding:0;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #e0e0e0; border-top:none;">
                      <tr style="background-color:#f8f9fa;">
                        <th style="padding:10px 12px; text-align:left; font-size:13px; font-weight:600; color:#333; border-bottom:2px solid #dee2e6; width:52%;">프로젝트명</th>
                        <th style="padding:10px 12px; text-align:center; font-size:13px; font-weight:600; color:#333; border-bottom:2px solid #dee2e6; width:16%;">근무지역</th>
                        <th style="padding:10px 12px; text-align:center; font-size:13px; font-weight:600; color:#333; border-bottom:2px solid #dee2e6; width:16%;">기간</th>
                        <th style="padding:10px 12px; text-align:center; font-size:13px; font-weight:600; color:#333; border-bottom:2px solid #dee2e6; width:16%;">마감일</th>
                      </tr>
                      ${rowsHtml}
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr><td style="height:16px;"></td></tr>`;
    }

    const totalCount = activeLevels.reduce((sum, l) => sum + assignedByLevel[l].length, 0);

    return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>프로젝트 안내</title>
</head>
<body style="margin:0; padding:0; background-color:#f4f4f4; font-family:'맑은 고딕','Malgun Gothic',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f4f4;">
    <tr>
      <td align="center" style="padding:20px 0;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff; border-radius:8px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.1);">

          <!-- 헤더 -->
          <tr>
            <td style="background-color:#ffffff; padding:28px 30px; text-align:center; border-bottom:3px solid #3273dc;">
              <table cellpadding="0" cellspacing="0" border="0" align="center"><tr>
                <td style="vertical-align:middle; padding-right:12px;"><img src="${SITE_URL}/wideworkLogo.png" alt="WIDE-WORK" style="height:40px; width:auto; display:block;" /></td>
                <td style="vertical-align:middle;"><h1 style="margin:0; color:#2c3e50; font-size:22px; font-weight:700; letter-spacing:1px;">프로젝트 안내</h1></td>
              </tr></table>
              <p style="margin:10px 0 0; color:#888; font-size:13px;">비전와이드에서 새로운 프로젝트를 안내드립니다</p>
            </td>
          </tr>

          <!-- 배너 링크 -->
          <tr>
            <td style="padding:0; font-size:0; line-height:0;">
              <table width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;">
                <tr>
                  <td width="172" style="padding:0; font-size:0; line-height:0;">
                    <a href="https://www.visionwide.kr/" target="_blank" style="display:block; border:0;">
                      <img src="${SITE_URL}/banner_link_1.png" alt="비전와이드" width="172" style="display:block; border:0; width:172px;" />
                    </a>
                  </td>
                  <td width="86" style="padding:0; font-size:0; line-height:0;">
                    <a href="https://widework.kr/" target="_blank" style="display:block; border:0;">
                      <img src="${SITE_URL}/banner_link_2.png" alt="와이드워크" width="86" style="display:block; border:0; width:86px;" />
                    </a>
                  </td>
                  <td width="82" style="padding:0; font-size:0; line-height:0;">
                    <a href="https://www.instagram.com/visionwide_" target="_blank" style="display:block; border:0;">
                      <img src="${SITE_URL}/banner_link_3.png" alt="인스타그램" width="82" style="display:block; border:0; width:82px;" />
                    </a>
                  </td>
                  <td width="88" style="padding:0; font-size:0; line-height:0;">
                    <a href="https://open.kakao.com/o/gnVZHaff" target="_blank" style="display:block; border:0;">
                      <img src="${SITE_URL}/banner_link_4.png" alt="카카오톡" width="88" style="display:block; border:0; width:88px;" />
                    </a>
                  </td>
                  <td width="172" style="padding:0; font-size:0; line-height:0;">
                    <a href="https://blog.naver.com/visionwide_" target="_blank" style="display:block; border:0;">
                      <img src="${SITE_URL}/banner_link_5.png" alt="블로그" width="172" style="display:block; border:0; width:172px;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- 인사말 -->
          <tr>
            <td style="padding:28px 30px 20px;">
              <p style="margin:0 0 12px; font-size:15px; color:#2c3e50; line-height:1.7;">
                안녕하세요, <strong>비전와이드</strong>입니다.<br>
                현재 진행중인 프로젝트 <strong>${totalCount}건</strong>을 안내드립니다.
              </p>
              <div style="height:1px; background-color:#eee; margin:16px 0;"></div>
            </td>
          </tr>

          <!-- 프로젝트 목록 -->
          <tr>
            <td style="padding:0 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                ${sectionsHtml}
              </table>
            </td>
          </tr>

          <!-- CTA 버튼 -->
          <tr>
            <td style="padding:24px 30px; text-align:center;">
              <a href="${SITE_URL}" style="display:inline-block; background-color:#3273dc; color:#ffffff; padding:14px 40px; font-size:15px; font-weight:600; text-decoration:none; border-radius:6px;" target="_blank">
                전체 프로젝트 보기
              </a>
            </td>
          </tr>

          <!-- 안내 문구 -->
          <tr>
            <td style="padding:0 30px 24px;">
              <div style="background-color:#f8f9fa; border-radius:6px; padding:16px; border-left:3px solid #3273dc;">
                <p style="margin:0; font-size:13px; color:#555; line-height:1.6;">
                  지원을 원하시면 프로젝트명을 클릭하여 상세 내용을 확인하시고 지원해 주세요.<br>
                  문의사항은 아래 연락처로 연락 부탁드립니다.
                </p>
              </div>
            </td>
          </tr>

          <!-- 푸터 -->
          <tr>
            <td style="background-color:#f8f9fa; padding:20px 30px; border-top:1px solid #eee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="text-align:center;">
                    <p style="margin:0 0 6px; font-size:13px; color:#555; font-weight:600;">(주)비전와이드</p>
                    <p style="margin:0 0 4px; font-size:12px; color:#888;">경기도 광명시 오리로854번길 12, 3층 304호(철산동, 세인트빌딩)</p>
                    <p style="margin:0 0 4px; font-size:12px; color:#888;">TEL: 02-565-5747 | E-mail: recruit@visionwide.kr</p>
                    <p style="margin:8px 0 0; font-size:11px; color:#aaa;">본 메일은 발신 전용이며, 문의사항은 위 연락처로 부탁드립니다.</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  }

  // HTML 복사
  async function copyHtml() {
    const html = generateEmailHtml();
    try {
      await navigator.clipboard.writeText(html);
      copySuccess = true;
      setTimeout(() => { copySuccess = false; }, 2000);
    } catch (err) {
      const ta = document.createElement('textarea');
      ta.value = html;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      copySuccess = true;
      setTimeout(() => { copySuccess = false; }, 2000);
    }
  }

  // 반응형: 생성된 HTML
  $: emailHtml = generateEmailHtml(assignments);

  onMount(() => {
    if (!isAdmin) {
      setPage('login');
      return;
    }
    loadProjects();
  });
</script>

<Navigation {setPage} {user} {onLogout} currentPage="admin-email-template" />

<div class="email-template-page">
  <div class="page-header">
    <h1>이메일 템플릿 생성기</h1>
    <p class="page-desc">프로젝트를 등급별로 배정하여 HTML 이메일 템플릿을 생성합니다. 버튼 클릭 또는 드래그&드롭으로 배정하세요.</p>
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="spinner"></div>
      <p>프로젝트를 불러오는 중...</p>
    </div>
  {:else if errorMessage}
    <div class="error-message">{errorMessage}</div>
  {:else}
    <div class="top-section">
      <!-- 왼쪽: 프로젝트 리스트 -->
      <div class="project-list-panel">
        <div class="panel-header">
          <h2>진행중인 프로젝트 ({projects.length}건)</h2>
        </div>
        <div class="search-bar">
          <input
            type="text"
            placeholder="프로젝트 검색..."
            bind:value={searchText}
            class="search-input"
          />
          {#if searchText}
            <button class="search-clear" on:click={() => searchText = ''}>x</button>
          {/if}
        </div>
        <div class="project-scroll-list">
          {#each filteredProjects as project (project.id)}
            <div
              class="project-row"
              class:assigned={assignments[project.id]}
              draggable="true"
              on:dragstart={(e) => handleDragStart(e, project.id)}
              on:dragend={handleDragEnd}
            >
              <div class="project-info">
                <span class="project-title">{project.title}</span>
                <span class="project-meta">
                  {project.salary || '-'} | 마감: {project.deadline ? formatDateFull(project.deadline) : '-'}
                  {#if project.level}
                    | {project.level}
                  {/if}
                </span>
              </div>
              <div class="assign-buttons">
                {#each levels as level}
                  <button
                    class="assign-btn"
                    class:active={assignments[project.id] === level}
                    style="--level-color: {levelColors[level]};"
                    on:click={() => assignToLevel(project.id, level)}
                    title="{level}에 배정"
                  >{level}</button>
                {/each}
              </div>
            </div>
          {/each}
          {#if filteredProjects.length === 0}
            <div class="empty-msg">검색 결과가 없습니다.</div>
          {/if}
        </div>
      </div>

      <!-- 오른쪽: 등급별 드롭존 -->
      <div class="dropzone-panel">
        <div class="panel-header">
          <h2>등급별 배정 ({totalAssigned}건)</h2>
          {#if totalAssigned > 0}
            <button class="btn-sm btn-secondary" on:click={clearAll}>전체 해제</button>
          {/if}
        </div>
        <div class="dropzones">
          {#each levels as level}
            <div
              class="dropzone"
              class:drag-over={dragOverLevel === level}
              style="--level-color: {levelColors[level]};"
              on:dragover={(e) => handleDragOver(e, level)}
              on:dragleave={(e) => handleDragLeave(e, level)}
              on:drop={(e) => handleDrop(e, level)}
            >
              <div class="dropzone-header">
                <span class="level-badge" style="background-color: {levelColors[level]};">{level}</span>
                <span class="dropzone-count">{assignedByLevel[level].length}건</span>
              </div>
              <div class="dropzone-items">
                {#if assignedByLevel[level].length === 0}
                  <div class="dropzone-empty">여기에 드래그하거나<br>왼쪽 버튼을 클릭하세요</div>
                {:else}
                  {#each assignedByLevel[level] as project (project.id)}
                    <div class="dropzone-item">
                      <span class="dropzone-item-title">{project.title}</span>
                      <button class="remove-btn" on:click={() => removeFromLevel(project.id)} title="제거">x</button>
                    </div>
                  {/each}
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>

    <!-- 하단: 미리보기/HTML 소스 -->
    <div class="preview-panel">
      <div class="panel-header">
        <h2>{showPreview ? '미리보기' : 'HTML 소스'}</h2>
        <div class="preview-actions">
          <button
            class="btn-sm"
            class:btn-primary={showPreview}
            class:btn-secondary={!showPreview}
            on:click={() => showPreview = true}
          >미리보기</button>
          <button
            class="btn-sm"
            class:btn-primary={!showPreview}
            class:btn-secondary={showPreview}
            on:click={() => showPreview = false}
          >HTML 소스</button>
          <button class="btn-sm btn-success" on:click={copyHtml}>
            {copySuccess ? '복사됨!' : 'HTML 복사'}
          </button>
        </div>
      </div>
      <div class="preview-content">
        {#if showPreview}
          <iframe
            bind:this={iframeEl}
            srcdoc={emailHtml}
            title="이메일 미리보기"
            class="preview-iframe"
            sandbox="allow-same-origin"
          ></iframe>
        {:else}
          <pre class="html-source">{emailHtml}</pre>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .email-template-page {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .page-header h1 {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 8px;
  }

  .page-desc {
    color: #666;
    font-size: 14px;
    margin: 0;
  }

  .loading-container {
    text-align: center;
    padding: 60px 20px;
    color: #666;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e9ecef;
    border-top-color: #3273dc;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-message {
    background: #fee;
    color: #c00;
    padding: 16px;
    border-radius: 8px;
    text-align: center;
  }

  /* 상단 2컬럼 */
  .top-section {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 24px;
    align-items: start;
    margin-bottom: 24px;
  }

  /* 패널 공통 */
  .project-list-panel,
  .dropzone-panel,
  .preview-panel {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #eee;
    background: #fafafa;
    flex-wrap: wrap;
    gap: 8px;
  }

  .panel-header h2 {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  /* 검색 */
  .search-bar {
    padding: 10px 16px;
    border-bottom: 1px solid #eee;
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    box-sizing: border-box;
  }

  .search-input:focus {
    outline: none;
    border-color: #3273dc;
  }

  .search-clear {
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    font-size: 14px;
    padding: 4px;
  }

  /* 프로젝트 리스트 */
  .project-scroll-list {
    max-height: 480px;
    overflow-y: auto;
  }

  .project-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-bottom: 1px solid #f0f0f0;
    cursor: grab;
    transition: background 0.15s;
  }

  .project-row:active {
    cursor: grabbing;
  }

  .project-row:hover {
    background: #f8f9fa;
  }

  .project-row.assigned {
    background: #f0f7ff;
  }

  .project-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .project-title {
    font-size: 13px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .project-meta {
    font-size: 11px;
    color: #999;
  }

  .assign-buttons {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  .assign-btn {
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 600;
    border: 1.5px solid var(--level-color);
    border-radius: 4px;
    cursor: pointer;
    background: #fff;
    color: var(--level-color);
    font-family: inherit;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .assign-btn:hover {
    background: var(--level-color);
    color: #fff;
  }

  .assign-btn.active {
    background: var(--level-color);
    color: #fff;
  }

  .empty-msg {
    padding: 30px;
    text-align: center;
    color: #aaa;
    font-size: 13px;
  }

  /* 드롭존 패널 */
  .dropzones {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .dropzone {
    border-bottom: 1px solid #eee;
    transition: background 0.2s;
  }

  .dropzone:last-child {
    border-bottom: none;
  }

  .dropzone.drag-over {
    background: #eef3fc;
    box-shadow: inset 0 0 0 2px var(--level-color);
  }

  .dropzone-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
  }

  .level-badge {
    display: inline-block;
    padding: 2px 10px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    border-radius: 3px;
  }

  .dropzone-count {
    font-size: 12px;
    color: #888;
  }

  .dropzone-items {
    min-height: 60px;
    padding: 6px 12px;
  }

  .dropzone-empty {
    padding: 16px;
    text-align: center;
    color: #bbb;
    font-size: 12px;
    line-height: 1.6;
    border: 2px dashed #e0e0e0;
    border-radius: 6px;
    margin: 4px;
  }

  .dropzone-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    margin: 3px 0;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    font-size: 12px;
  }

  .dropzone-item-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #333;
  }

  .remove-btn {
    background: none;
    border: none;
    color: #ccc;
    cursor: pointer;
    font-size: 14px;
    padding: 0 4px;
    line-height: 1;
    flex-shrink: 0;
  }

  .remove-btn:hover {
    color: #e74c3c;
  }

  /* 미리보기 */
  .preview-actions {
    display: flex;
    gap: 6px;
  }

  .preview-content {
    min-height: 400px;
  }

  .preview-iframe {
    width: 100%;
    height: 700px;
    border: none;
    display: block;
  }

  .html-source {
    margin: 0;
    padding: 16px 20px;
    font-size: 12px;
    line-height: 1.5;
    background: #1e1e1e;
    color: #d4d4d4;
    overflow: auto;
    max-height: 700px;
    white-space: pre-wrap;
    word-break: break-all;
  }

  /* 버튼 */
  .btn-sm {
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    background: #fff;
    color: #555;
    font-family: inherit;
    transition: all 0.15s;
  }

  .btn-sm:hover {
    background: #f0f0f0;
  }

  .btn-primary {
    background: #3273dc;
    color: #fff;
    border-color: #3273dc;
  }

  .btn-primary:hover {
    background: #2563c7;
  }

  .btn-secondary {
    background: #f0f0f0;
    color: #555;
    border-color: #ddd;
  }

  .btn-success {
    background: #27ae60;
    color: #fff;
    border-color: #27ae60;
  }

  .btn-success:hover {
    background: #219a52;
  }

  /* 반응형 */
  @media (max-width: 960px) {
    .top-section {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .email-template-page {
      padding: 16px;
    }

    .panel-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .preview-iframe {
      height: 500px;
    }

    .assign-buttons {
      flex-direction: column;
    }
  }
</style>
